import PersonContact from "features/contacts/blocks/PersonContact";
import type { ContactStatus } from "features/contacts/blocks/PersonContact/PersonContact.types";
import type {
  ContactsListApiResponse,
  ContactsListResponse,
  PersonShort,
} from "features/contacts/data/types";
import { isErrorResponse } from "features/contacts/data/utils";
import { useDataQuery } from "hooks/useData";
import { useUser } from "hooks/useUser";
import React, { useEffect, useMemo, useState } from "react";

import { getStatusKey, removeFromAllBuckets } from "./ContactList.utils";

const ContactList: React.FC = () => {
  const { isLoggedIn, personId } = useUser();
  const contactsData = useDataQuery<ContactsListApiResponse>(
    "contacts/list/",
    !isLoggedIn,
    null,
  );

  const [contacts, setContacts] = useState<ContactsListResponse | null>(null);

  useEffect(() => {
    if (contactsData && !isErrorResponse(contactsData)) {
      setContacts(contactsData);
    }
  }, [contactsData]);

  const counts = useMemo(
    () => ({
      mutual: contacts?.mutual_contacts.length ?? 0,
      myRequests: contacts?.my_requests.length ?? 0,
      theirRequests: contacts?.their_requests.length ?? 0,
    }),
    [contacts],
  );

  const handleActionDone = (
    person: PersonShort,
    _currentStatus: ContactStatus,
    nextStatus: ContactStatus,
  ) => {
    setContacts((prev) => {
      if (!prev) return prev;
      const cleaned = removeFromAllBuckets(prev, person.id);
      const targetKey = getStatusKey(nextStatus);

      if (!targetKey) return cleaned;

      return { ...cleaned, [targetKey]: [person, ...cleaned[targetKey]] };
    });
  };

  if (!isLoggedIn || !personId)
    return <div>Для просмотра контактов нужна авторизация.</div>;
  if (!contactsData) return <div>Загрузка контактов...</div>;
  if (isErrorResponse(contactsData)) return <div>{contactsData.error}</div>;
  if (!contacts) return <div>Загрузка контактов...</div>;

  const hasAnyContacts =
    counts.mutual + counts.myRequests + counts.theirRequests > 0;

  if (!hasAnyContacts)
    return <div style={{ marginTop: "12px" }}>Список контактов пуст.</div>;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {contacts.mutual_contacts.length > 0 && (
        <section>
          <h3>Контакты</h3>
          <div style={{ display: "grid", gap: 10 }}>
            {contacts.mutual_contacts.map((person) => (
              <PersonContact
                key={`mutual-${person.id}`}
                personId={person.id}
                contactStatus="mutual"
                onActionDone={(nextStatus) =>
                  handleActionDone(person, "mutual", nextStatus)
                }
              />
            ))}
          </div>
        </section>
      )}
      {contacts.my_requests.length > 0 && (
        <section>
          <h3>Отправленные запросы</h3>
          <div style={{ display: "grid", gap: 10 }}>
            {contacts.my_requests.map((person) => (
              <PersonContact
                key={`my-request-${person.id}`}
                personId={person.id}
                contactStatus="my_request"
                onActionDone={(nextStatus) =>
                  handleActionDone(person, "my_request", nextStatus)
                }
              />
            ))}
          </div>
        </section>
      )}
      {contacts.their_requests.length > 0 && (
        <section>
          <h3>Входящие запросы</h3>
          <div style={{ display: "grid", gap: 10 }}>
            {contacts.their_requests.map((person) => (
              <PersonContact
                key={`their-request-${person.id}`}
                personId={person.id}
                contactStatus="their_request"
                onActionDone={(nextStatus) =>
                  handleActionDone(person, "their_request", nextStatus)
                }
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default ContactList;
